import { SessionStorageManager, StorageManager } from '@speeki/storage-manager';

import { LanguageCode, TenantModulesQuery } from '@graphql/generated/graphql';

import { ServicesStatistics } from '@utils/hooks/useServicesStatistics';

import { GetPathOptions } from '@services/RouteService/paths';

import { makeVar } from '@apollo/client';
import { GuestLoginFormValues } from '@domains/auth/organisms/GuestLoginForm/GuestLoginForm';
import { SignUpFormValues } from '@domains/auth/pages/SignUpPage/SignUpPage';
import { SwitchCompanyMetadata } from '@domains/dashboard/modals/SwitchCompanyModal/SwitchCompanyModal';
import { SelectSubsidiarySiteModalValues } from '@domains/new-conversation/organisms/SelectSubsidiarySitesModal/useSelectSubsidiarySitesModal';
import { DetailsSchemaForm } from '@domains/new-conversation/useConversationForm';

/**
 * Variable for storing the information about initial loading (AppLateInit.tsx).
 */
export const initLoadingVar = makeVar<boolean>(true);

/**
 * Variable for storing the information about user's authentication.
 */
export const authVar = makeVar<{
    isGuest: boolean;
    isLoggedIn: boolean;
    isMfa: boolean;
}>({
    isGuest: false,
    isLoggedIn: false,
    isMfa: false,
});

/**
 * Variable for storing the information about tenant's modules.
 */
export const tenantModulesVar = makeVar<
    TenantModulesQuery['tenant']['modules']
>({
    cases: false,
    disclosures: false,
    subsidiarySites: false,
    surveys: false,
});

/**
 * Variable for redirecting a guest user to disclosure list page after logging in as an user.
 */
export const redirectToDisclosureVar = makeVar<boolean>(false);

/**
 * Variable for storing the email for forgot password flow.
 */
export const forgotPasswordEmailVar = makeVar<string>('');

/**
 * Variable for storing the information whether initialize session action is in progress.
 * Do not reset that value inside resetVar!
 */
export const sessionProcessingVar = makeVar<boolean>(false);

/**
 * Variable for checking if user is on the report speeki site using MsTeams App
 */
export const isUsingMsTeamsVar = makeVar<boolean>(false);

/**
 * Variable for storing the information whether logout action is in progress.
 * Do not reset that value inside resetVar!
 */

export const logoutProcessingVar = makeVar<boolean>(false);

/**
 * Variable for storing guest login form data
 */
export const guestLoginFormDataVar = makeVar<GuestLoginFormValues | null>(null);

/**
 * Variable for storing signup form data
 */
export const signUpFormDataVar = makeVar<SignUpFormValues | null>(null);

/**
 * Variable for storing the information whether logout action is in progress.
 * Do not reset that value inside resetVar!
 * TODO: SPD-2948 Handling loading & status
 * TODO: find better place for type and setApolloActionStatus
 */
type ApolloActionStatus = 'error' | 'idle' | 'success' | 'loading';

export const setApolloActionStatus = (status: {
    error?: boolean;
    loading?: boolean;
    success?: boolean;
}): ApolloActionStatus => {
    if (status.loading) return 'loading';
    if (status.error) return 'error';
    if (status.success) return 'success';
    return 'idle';
};

export const translationsStatusVar = makeVar<
    'error' | 'idle' | 'success' | 'loading'
>('idle');

export const anonymityStatusVar = makeVar<
    'error' | 'idle' | 'success' | 'loading'
>('idle');

/**
 * Variable for storing the information about current conversation details.
 */
export const conversationDetailsVar = makeVar<DetailsSchemaForm | undefined>(
    undefined,
);
/**
 * Variable for storing the information about current conversation submit loading status.
 */
export const conversationSubmitLoadingVar = makeVar<boolean | undefined>(false);

/**
 * Normalised email addresses the reporter has already confirmed in this session.
 *
 * The backend confirms per (session, address), so once an address is confirmed we must
 * not ask for a code again - notably when the same address is used both as the identity
 * address and as the updates address.
 */
export const verifiedReporterEmailsVar = makeVar<string[]>([]);

/**
 * Clears everything held about the reporter behind the current report.
 *
 * Both variables have the same lifetime - they are the reporter's own data for one
 * report - so they must be dropped together. `resetVars` only runs on logout, which an
 * anonymous reporter never reaches, so this is what actually ends a reporter's data on
 * a shared or kiosk machine. Use it instead of clearing `conversationDetailsVar` alone.
 */
export const resetReporterDetails = () => {
    conversationDetailsVar(undefined);
    verifiedReporterEmailsVar([]);
};

/**
 * Variable for storing the information about current conversation details.
 */
export const subsidiarySiteVar = makeVar<
    (SelectSubsidiarySiteModalValues & { tenantUuid: string }) | undefined
>(undefined);

/**
 * Variable for storing the information about subsidiary site uuid from url.
 */
export const subsidiarySiteUuidFromUrlVar = makeVar<string | undefined>(
    SessionStorageManager.getValue('subsidiarySite') || undefined,
);

/**
 * Variable for storing the information about current language.
 * Do not reset that value inside resetVar!
 */
export const currentLanguageVar = makeVar<LanguageCode>(
    (StorageManager.getValue('language')?.toUpperCase() as LanguageCode) ||
        'EN',
);

/**
 * Do not reset that value inside resetVar!
 */
export const forceDefaultNavigationVar = makeVar<boolean>(false);

/**
 * Variable for storing pin for case details.
 */
export const guestPinVar = makeVar<string | undefined>(undefined);

/**
 * Variable for storing disclosureUuid for ChangePersonalInfoModal
 */
export const personalInfoDisclosureUuidVar = makeVar<string | undefined>(
    undefined,
);

/**
 * Variable for storing metadata for SwitchCompanyModal
 */
export const switchCompanyMetadataVar = makeVar<
    SwitchCompanyMetadata | undefined
>(undefined);

/**
 * Variable for storing the information about logged in user's services statistics
 */
export const servicesStatistics = makeVar<ServicesStatistics>({
    casesLoading: false,
    disclosuresCount: 0,
    disclosuresLoading: false,
    ongoingCases: 0,
    respondedDisclosures: 0,
    surveysCount: 0,
    surveysLoading: false,
});

/**
 * Variable for redirect unlogged uset to proper path after login/sign up/guest login
 */
export const redirectUnloggedUser = makeVar<GetPathOptions>({
    path: '/',
});

/**
 * Variable is only for preventing double modal show in disclosure details
 */
export const alreadyFilledOutPersonalInfoVar = makeVar(false);

export const resetVars = () => {
    authVar({
        isGuest: false,
        isLoggedIn: false,
        isMfa: false,
    });
    forgotPasswordEmailVar('');
    redirectToDisclosureVar(false);
    resetReporterDetails();
    subsidiarySiteVar(undefined);
    redirectUnloggedUser({
        path: '/',
    });
};
